import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { Column, ColumnKeys, DataGridItemList, GlobalExclusionsEvents } from '../types';
import { WEB_COMPONENT_CONFIGURATION_PROFILES, WEB_COMPONENT_EXCLUSION_LIST_SELECTOR, WEB_COMPONENT_EXPORT_EXCLUSIONS_PANEL, WEB_COMPONENT_GRID_VIEW } from '../../../webcomponents.constants';
import { GridViewWebComponent } from '../../../grid/gridView';
import { GlobalExclusionsGzDataProvider } from '../dataProvider/GlobalExclusionsGzDataProvider';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { ExclusionListSelectorWebComponent } from '../../exclusionListSelector';
import { GzDialog, GzDialogEvents } from '../../../ui/gzDialog/component/GzDialog';
import { EnumListEvents } from '../../../ui/gzEnumList/types';
import { TableItem } from '../../../ui/gzTable/types';
import { GzTableWebComponent } from '../../../ui/gzTable';
import { ExportExclusionsPanelWebComponent } from '../../exclusionsExportPanel';
import { Rights } from '@nirvana/shared/constants';
import { translateFn } from '../../../utils/translator.helper';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { GridTitleConfig, GridTitleConfigEvent } from '../../../grid/uiElements/gridViewTitle/types';
import { GlobalExclusionsGridConfig } from './GlobalExclusionsGridConfig';

export class GlobalExclusionsView extends BaseWebComponentView {

  public dom: {
    $wrapper: HTMLElement;
    $gridView: GridViewWebComponent<Column, ColumnKeys>;
    $addExclusions: GzButtonWebComponent;
    $assignToList: GzButtonWebComponent;
    $exportSelection: GzButtonWebComponent;
    $deleteExclusions: GzButtonWebComponent;
  };

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$gridView = this.shadow.querySelector(WEB_COMPONENT_GRID_VIEW);
    this.dom.$addExclusions = this.shadow.querySelector('#addExclusions');
    this.dom.$assignToList = this.shadow.querySelector('#assignToLists');
    this.dom.$exportSelection = this.shadow.querySelector('#exportSelection');
    this.dom.$deleteExclusions = this.shadow.querySelector('#deleteExclusions');

    if (this.dom.$gridView.injectDependencies) {
      this.dom.$gridView.injectDependencies(this.dependencies);
      this.dom.$gridView.start().then().catch(console.error);
    }

    this.setTranslatedStrings();
  }

  setTranslatedStrings() {
    this.dom.$addExclusions.innerText = translateFn('Add exclusions');
    this.dom.$assignToList.innerText = translateFn('Assign to lists');
    this.dom.$exportSelection.innerText = translateFn('Export selection');
    this.dom.$deleteExclusions.innerText = translateFn('Delete');
  }

  buildView(data: any) {
    if (!data) {
      this.dom.$wrapper.innerHTML = '';
      return;
    }

    this.buildGridView(data);
    this.buildActionSelectList();
  }

  private buildGridView(data: any) {
    this.dom.$addExclusions.innerText = !data.response.activeExclusionList?.label ? translateFn('Add exclusions') : translateFn('Edit List');

    const title = data.response.activeExclusionList?.label || translateFn('All Exclusions');
    const isLink = !!data.response.activeExclusionList?.label;
    const event = new GridTitleConfigEvent().withClickEvent(GlobalExclusionsEvents.LINK_TITLE_CLICK);
    const gridSettingsPanelParent = window.document.querySelector(WEB_COMPONENT_CONFIGURATION_PROFILES)?.shadowRoot?.querySelector('.wrapper');

    this.dom.$gridView.propertiesSet = false;

    this.dom.$gridView.sideMenuParent = gridSettingsPanelParent as HTMLElement;
    this.dom.$gridView.extraParams = {companyId: Application.Main.getActiveCompany().id, listId: data.response.activeExclusionList?.value || ""};
    this.dom.$gridView.titleConfig = new GridTitleConfig(title, isLink, event);

    (new GlobalExclusionsGridConfig(new GlobalExclusionsGzDataProvider(this.dependencies), this.dom.$gridView)).setGridViewMetadata(this.dom.$gridView);

    if (Array.isArray(this.dom.$gridView.extraParams) && !this.dom.$gridView.extraParams.length) {
      // Set extraParams to empty object when having the default empty array
      this.dom.$gridView.extraParams = {};
    }

    this.dom.$gridView.propertiesSet = true;
  }

  private buildActionSelectList() {
    this.dom.$deleteExclusions.disabled = !Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE]);
  }

  public showExportExclusionsWindow(selectedExclusions) {
    const windowBody = document.createElement('div');
    windowBody.setAttribute('style', 'height: 100%');
    windowBody.innerHTML = `<gz-export-exclusion-panel></gz-export-exclusion-panel>`;

    const exportExclusionsPanel = windowBody.querySelector(
      WEB_COMPONENT_EXPORT_EXCLUSIONS_PANEL
    ) as ExportExclusionsPanelWebComponent;

    const dependencies = this.dependencies;
    dependencies.selectedExclusions = selectedExclusions;
    exportExclusionsPanel.injectDependencies(dependencies);

    exportExclusionsPanel
      .start()
      .then(() => {
        console.log('Export Exclusions Panel started.');
      })
      .catch(console.error);

    const windowConfigs: GzDialog = {
      headline: translateFn('Export exclusions to CSV'),
      bodyContent: windowBody,
      isOpen: true,
      isWindow: true,
    };

    const configurationProfiles = document.body.querySelector(WEB_COMPONENT_CONFIGURATION_PROFILES)
      ?.parentElement as HTMLElement;
    const dialog = GzDialogWebComponent.buildDialogElement(windowConfigs, configurationProfiles);

    exportExclusionsPanel.addEventListener(GzDialogEvents.CANCEL, () => {
      dialog.close();
    });
  }

  public showExclusionItemAssignToList(exclusionId, assignedExclusionsListsIds, confirmCallback) {
    const windowBodyHtml = document.createElement('div');
    windowBodyHtml.classList.add('px-5', 'py-3');
    windowBodyHtml.innerHTML = `
    <gz-text fontWeight="400" fontSize="14" lineHeight="1.2">${translateFn('Choose the lists where to include the selected exclusion rule.')}
    </gz-text><br>
    <div><gz-exclusion-list-selector></gz-exclusion-list-selector></div><br><br>
    <gz-title class="title" fontweight="400" fontsize="18">${translateFn('Lists preview')}</gz-title>`;

    const impact = document.createElement('div');
    const table = this.createImpactTable();

    let selectedExclusionsList = [];
    const selector = windowBodyHtml.querySelector(
      WEB_COMPONENT_EXCLUSION_LIST_SELECTOR
    ) as ExclusionListSelectorWebComponent;

    selector.setAttribute('data-selected-target', JSON.stringify(assignedExclusionsListsIds));
    selector.injectDependencies(this.dependencies);
    selector.addEventListener(EnumListEvents.SELECTION_CHANGE, (e: CustomEvent) => {
      // @ts-ignore
      const dataExclusionList = JSON.parse(e.currentTarget.dataset.exclusionLists) || [];
      selectedExclusionsList = e.detail.selectedValues.map(exclusionIndex => {
        return dataExclusionList[exclusionIndex]['value'];
      });

      impact.appendChild(table);
      windowBodyHtml.appendChild(impact);
      this.updateImpactTable(table, selectedExclusionsList, dataExclusionList);
    });
    selector
      .start()
      .then(() => {
        // if there are any lists previously linked to this exclusion, make sure to display the table containing them
        if (assignedExclusionsListsIds.length > 0) {
          impact.appendChild(table);
          windowBodyHtml.appendChild(impact);
          this.updateImpactTable(table, assignedExclusionsListsIds, selector.exclusionList);
        }
      })
      .catch(console.error);

    const windowFooterHtml = document.createElement('div');
    windowFooterHtml.innerHTML = `<gz-button id='confirm' type='full' class='mr-2'>
       ${translateFn('Save#preserve state')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${translateFn('CANCEL')}
      </gz-button>`;

    const windowTitle = translateFn('Edit list assignment');

    const windowConfigs: GzDialog = {
      headline: windowTitle,
      bodyContent: windowBodyHtml,
      footerContent: windowFooterHtml,
      isOpen: true,
      isWindow: true,
    };
    const configurationProfiles = document.body.querySelector(WEB_COMPONENT_CONFIGURATION_PROFILES)
      ?.parentElement as HTMLElement;
    const dialog = GzDialogWebComponent.buildDialogElement(windowConfigs, configurationProfiles);
    const footerConfirmBtn = windowFooterHtml.querySelector('#confirm');
    if (footerConfirmBtn) {
      footerConfirmBtn.addEventListener('click', () => {
        dialog.close();
        confirmCallback({ id: exclusionId, listsIds: selectedExclusionsList });
      });
    }

    const footerCancerBtn = windowFooterHtml.querySelector('#cancel');
    if (footerCancerBtn) {
      footerCancerBtn.addEventListener('click', () => {
        dialog.close();
      });
    }
  }

  updateImpactTable(table, selectedLists, lists) {
    table.data = [];
    const tableData: Array<TableItem> = [];
    selectedLists.forEach(selectedPolicy => {
      const list = lists.find(x => x.value === selectedPolicy);
      const tableItem = {
        label: list['label'],
        value: [list['description']],
      };

      tableData.push(tableItem);
    });
    table.data = tableData;
  }

  createImpactTable() {
    const table = new GzTableWebComponent();
    table.head = {
      header: [translateFn('List name'), translateFn('Description')],
      color: ['', ''],
    };
    return table;
  }

  public getDeleteConfirmationDialogFooterHtml(): string {
    return `<gz-button id='confirm' type='outline' color='negative' class='mr-2'>
        ${translateFn('DELETE')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${translateFn('CANCEL')}
      </gz-button>`;
  }

  public getDeleteConfirmationDialogBodyHtml(warningText, affectedLists, selectedItemsData): string {
    let affectedPoliciesIds: string[] = [];
    affectedLists.forEach(list => {
      affectedPoliciesIds.push(...list.policies.map(pol => pol.id));
    });
    affectedPoliciesIds = [...new Set(affectedPoliciesIds)]; // remove duplicated ids

    const numAffectedLists = affectedLists.length;
    const numAffectedPolicies = affectedPoliciesIds.length;
    const impactSummary = this.getImpactSummary(numAffectedLists, numAffectedPolicies, selectedItemsData);
    const impactDetails = this.getImpactDetails(affectedLists);

    return `<div>
      <p>${warningText}</p>
      <br>
      <p>${impactSummary}</p>
      ${impactDetails.outerHTML}
    </div>`;
  }

  private getImpactDetails(affectedLists: DataGridItemList[]): HTMLElement {
    const impactDetails = document.createElement('div');

    if (affectedLists.length === 0) {
      // If no lists to display, return empty div
      return impactDetails;
    }

    // Sort lists by the number of impacted policies in descending order
    affectedLists.sort((list1: DataGridItemList, list2: DataGridItemList): number => {
      return list2.policies.length - list1.policies.length;
    });

    // Display only the first 5 list names
    affectedLists.slice(0, 5).forEach(list => {
      const item = document.createElement('div');
      const listName = translateFn('List {listName}', { listName: list.name });
      const affectedPolicyNames = this.getPoliciesListText(list.policies);
      item.innerHTML = listName + affectedPolicyNames;
      impactDetails.appendChild(document.createElement('br'));
      impactDetails.appendChild(item);
    });

    // If more than 5 lists are affected, display "+ X more lists" on a new line
    if (affectedLists.length > 5) {
      const item = document.createElement('div');
      item.innerHTML = translateFn('+ {numExtraLists} more lists', {
        numExtraLists: affectedLists.length - 5,
      });
      impactDetails.appendChild(document.createElement('br'));
      impactDetails.appendChild(item);
    }

    return impactDetails;
  }

  private getPoliciesListText(policies): string {
    if (policies.length === 0) {
      return '';
    }

    // Display only the first 2 policy names
    const displayedPolicies = policies.slice(0, 2);
    const displayedPoliciesText = displayedPolicies.map(policy => policy.name).join(', ');

    if (policies.length === displayedPolicies.length) {
      return (
        ' ' +
        translateFn('(Policies: {policyNamesList})', {
          policyNamesList: displayedPoliciesText,
        })
      );
    }

    return (
      ' ' +
      translateFn('(Policies: {policyNamesList} and {numExtraPolicies} more policies)', {
        policyNamesList: displayedPoliciesText,
        numExtraPolicies: policies.length - displayedPolicies.length,
      })
    );
  }

  private getImpactSummary(numAffectedLists, numAffectedPolicies, selectedItemsData): string {
    const fixedValues: any = {
      numLists: `<strong>${numAffectedLists}</strong>`,
      numPolicies: `<strong>${numAffectedPolicies}</strong>`,
    };

    if (!selectedItemsData.length) {
      // Deletion has been made without using the selection checkboxes
      return translateFn('This action will impact {numLists} lists and {numPolicies} policies.', fixedValues);
    }

    fixedValues.numExclusions = `<strong>${selectedItemsData.length}</strong>`;

    return translateFn(
      'This action will impact {numExclusions} exclusions, {numLists} lists and {numPolicies} policies.',
      fixedValues
    );
  }
}
